import { useState } from 'react';
import api from '@/lib/api';
import { getDebugState } from '@/lib/telemetry';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const MAX_DESCRIPTION_LENGTH = 5000;

// Cap the message snapshot so a very long conversation can't balloon the report
// payload past the server bodyLimit. The most recent messages carry the
// diagnostic value; the durable server-side transcript holds the full record.
const MAX_REPORTED_MESSAGES = 500;

interface FiasAiReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current conversation id, or null for a temporary/unsaved chat. */
  conversationId: string | null;
  /** The current client-side message list (reported as conversation history). */
  messages: unknown[];
  /** The model in use, attached to the report for triage. */
  modelInfo?: unknown;
}

export function FiasAiReportProblemDialog({
  open,
  onOpenChange,
  conversationId,
  messages,
  modelInfo,
}: FiasAiReportProblemDialogProps) {
  const { toast } = useToast();
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedReportId, setSubmittedReportId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setDescription('');
    setSubmittedReportId(null);
    setCopied(false);
    onOpenChange(false);
  };

  const handleCopyReportId = async () => {
    if (!submittedReportId) return;
    await navigator.clipboard.writeText(submittedReportId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;

    setIsSubmitting(true);
    try {
      const trimmedMessages =
        messages.length > MAX_REPORTED_MESSAGES ? messages.slice(-MAX_REPORTED_MESSAGES) : messages;

      const result = await api.submitAssistantProblemReport(conversationId, {
        description: description.trim(),
        conversationHistory: trimmedMessages,
        modelInfo,
        telemetryState: getDebugState(),
      });

      setSubmittedReportId(result.reportId);
    } catch (err) {
      toast({
        title: 'Failed to submit report',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submittedReportId ? handleClose : onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        {submittedReportId ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Report Submitted
              </DialogTitle>
              <DialogDescription>
                Thank you for your feedback. Your report has been recorded.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="py-4">
                <label className="text-sm font-medium">Report ID</label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono select-all">
                    {submittedReportId}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyReportId}
                    className="shrink-0"
                  >
                    {copied ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </DialogBody>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Report a Problem</DialogTitle>
              <DialogDescription>
                Help us improve Fias AI by reporting a response or behavior that wasn&apos;t right.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="fias-ai-problem-description" className="text-sm font-medium">
                    What went wrong?
                  </label>
                  <Textarea
                    id="fias-ai-problem-description"
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))
                    }
                    placeholder="Describe what you expected versus what happened..."
                    rows={5}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {description.length}/{MAX_DESCRIPTION_LENGTH}
                  </p>
                </div>

                <p className="text-xs text-muted-foreground">
                  This report includes your recent conversation with Fias AI to help diagnose the
                  issue. No personal data beyond your user ID is included.
                </p>
              </div>
            </DialogBody>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!description.trim() || isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Report
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
